import {
  type ChangeEvent,
  type FormEvent,
  type MouseEvent,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { getFilteredEmailDomains } from '~/pages/email/lib/helpers'
import { PageId } from '~/shared/constants/pagesConstants'
import { useBulkSelector } from '~/shared/hooks/useBulkSelector'
import { useEmailInputField } from '~/shared/hooks/useEmailInputField'
import { eventLogger } from '~/shared/services/eventLogger'
import { resetErrorAction } from '~/shared/store/common/actions'
import { selectActionList } from '~/shared/store/common/selects'
import { GET_STATUS, SEND_USER_EMAIL } from '~/shared/store/user/actionTypes'
import {
  getUserAccountStatusAction,
  resendUserInstructionsAction,
  sendUserConfigAction,
  sendUserEmailAction,
  setUserAccountStatusAction,
} from '~/shared/store/user/actions'
import {
  selectExistingUUIDByEmail,
  selectHasAccessToAppByEmail,
  selectHasAccountByEmail,
} from '~/shared/store/user/selects'
import { appHistory } from '~/shared/utils'

const useSelectors = () =>
  useBulkSelector({
    fetchingActionsList: selectActionList,
    hasAccountByEmail: selectHasAccountByEmail,
    hasAccessToAppByEmail: selectHasAccessToAppByEmail,
    existingUUIDByEmail: selectExistingUUIDByEmail,
  })

export const useEmail = () => {
  const [areEmailTipsVisible, setAreEmailTipsVisible] = useState(false)
  const [isErrorModalShown, setIsErrorModalShown] = useState(false)
  const [isExistsModalShown, setIsExistsModalShown] = useState(false)

  const dispatch = useDispatch()
  const { search } = useLocation()
  const {
    fetchingActionsList,
    hasAccessToAppByEmail,
    hasAccountByEmail,
    existingUUIDByEmail,
  } = useSelectors()

  const [{ value: inputValue, isValid: isValidInputValue }, , validateEmail] =
    useEmailInputField()
  const deferredEmail = useDeferredValue(inputValue)

  const isStatusFetching = useMemo(
    () =>
      fetchingActionsList?.includes(SEND_USER_EMAIL) ||
      fetchingActionsList?.includes(GET_STATUS),
    [fetchingActionsList],
  )

  const isButtonDisabled = useMemo(
    () => !inputValue || !isValidInputValue || isStatusFetching,
    [isValidInputValue, inputValue, isStatusFetching],
  )

  const filteredEmailDomains = useMemo(
    () => getFilteredEmailDomains(deferredEmail),
    [deferredEmail],
  )

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (!isValidInputValue || isStatusFetching) return

      dispatch(getUserAccountStatusAction(inputValue))
    },
    [dispatch, isValidInputValue, inputValue, isStatusFetching],
  )

  const handleChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      validateEmail(target.value)
      setAreEmailTipsVisible(true)
    },
    [validateEmail],
  )

  const handlePrefilledEmail = useCallback(
    ({ currentTarget }: MouseEvent<HTMLButtonElement>) => {
      validateEmail(currentTarget.value)
      setAreEmailTipsVisible(false)
    },
    [validateEmail],
  )

  const handleTipsClick = useCallback(
    ({ currentTarget }: MouseEvent<HTMLButtonElement>) => {
      const [userName] = inputValue.split('@')

      validateEmail(`${userName}${currentTarget.value}`)
      setAreEmailTipsVisible(false)
    },
    [inputValue, validateEmail],
  )

  const handleCloseErrorModal = useCallback(() => {
    dispatch(resetErrorAction())
    setIsErrorModalShown(false)
  }, [dispatch])

  const handleCloseExistsModal = useCallback(() => {
    setIsExistsModalShown(false)
    dispatch(
      setUserAccountStatusAction({
        existingUuid: '',
        hasAccount: null,
        hasAccessToApp: false,
      }),
    )
  }, [dispatch])

  const handleResendInstructions = useCallback(
    (pageId: PageId) => {
      dispatch(resendUserInstructionsAction(existingUUIDByEmail))
      dispatch(
        setUserAccountStatusAction({
          existingUuid: '',
          hasAccount: null,
          hasAccessToApp: hasAccessToAppByEmail,
        }),
      )
      dispatch(sendUserConfigAction({ target_uuid: existingUUIDByEmail }))
      appHistory.goTo({ pathname: pageId, search })
    },
    [dispatch, existingUUIDByEmail, search, hasAccessToAppByEmail],
  )

  const saveEmail = useCallback(() => {
    dispatch(
      sendUserEmailAction({
        email: inputValue,
        unsuccessCallback: () => setIsErrorModalShown(true),
      }),
    )
  }, [dispatch, inputValue])

  useEffect(() => {
    if (hasAccountByEmail === null || !isValidInputValue) return

    const isNewUser = !hasAccountByEmail && !hasAccessToAppByEmail
    const isExistingUser = hasAccountByEmail || hasAccessToAppByEmail

    if (isNewUser) {
      window?.axon?.('track', 'sign_up', { method: 'email' })
      saveEmail()
    } else if (isExistingUser) {
      setIsExistsModalShown(true)
    }
  }, [hasAccessToAppByEmail, hasAccountByEmail, saveEmail, isValidInputValue])

  useEffect(() => {
    eventLogger.logEmailPageShown()
  }, [])

  return {
    areEmailTipsVisible,
    isErrorModalShown,
    isExistsModalShown,
    isButtonDisabled,
    isStatusFetching,
    inputValue,
    isValidInputValue,
    filteredEmailDomains,
    handleSubmit,
    handleChange,
    handleCloseErrorModal,
    handleCloseExistsModal,
    handleResendInstructions,
    handlePrefilledEmail,
    handleTipsClick,
  }
}
